import { Injectable, inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import { every as _every, uniqBy as _uniqBy } from 'lodash/fp'
import { v4 as uuid } from 'uuid'

import {
  EMPTY,
  catchError,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import { AiAnalystActions } from '@flowx/designer/modules/ai/agents/analyst/store/analyst.actions'
import { AiArchitectActions } from '@flowx/designer/modules/ai/agents/architect/store/architect.actions'
import { AiAssistantActions } from '@flowx/designer/modules/ai/agents/assistant/store/assistant.actions'
import { AiDesignerActions } from '@flowx/designer/modules/ai/agents/designer/store/designer.actions'
import { AiDeveloperActions } from '@flowx/designer/modules/ai/agents/developer/store/developer.actions'
import { ConversationApiService } from '@flowx/designer/modules/ai/common/api/conversation-api.service'
import {
  AddThreadMessageRequest,
  PatchMessageContextRequest,
  ReplaceMessageExecutionPlanRequest,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AiConversationContextBuilderService } from '@flowx/designer/modules/ai/conversation/ai-conversation-context-builder.service'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import { MessageType } from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { AiConversationService } from '@flowx/designer/modules/ai/conversation/ai-conversation.service'
import { AiExecutionPlanService } from '@flowx/designer/modules/ai/conversation/ai-execution-plan.service'
import {
  ConversationErrorActions,
  ExecutionPlanUpdateType,
} from '@flowx/designer/modules/ai/conversation/conversation-error.actions'
import {
  ConversationActions,
  TypeAreaActions,
} from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { ProcessActions } from '@flowx/designer/shared/store/actions/process.actions'

import { getConversationMapKey } from './conversation.constants'
import { conversationProcessMapKey } from './conversation.reducer'
import { ConversationSelectors } from './conversation.selectors'
import {
  applyExecutionPlanChanges,
  assignNewTargetsToCurrentExecutionPlan,
  getExecutionPlanEnhanceDiff,
  isConversationInScope,
  isEmptyExecutionPlan,
} from './conversation.utils'
import { ExecutionPlanActions } from './execution-plan.actions'

import { AiConversationServiceActions } from '../store/ai.actions'
import { UploadContextFile } from '../types/ai.types'

@Injectable()
export class ConversationEffects {
  #actions$ = inject(Actions)
  #store = inject(Store)
  #aiConversationService = inject(AiConversationService)
  #conversationApiService = inject(ConversationApiService)
  #flxModalService = inject(FlxModalService)
  #aiConversationContextBuilderService = inject(AiConversationContextBuilderService)
  #aiExecutionPlanService = inject(AiExecutionPlanService)

  // Track the source message being handled to prevent infinite loops from error messages
  #currentlyHandlingErrorForMessage: string | null = null

  checkAndLoadConversation = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.processNavigated),
      map(({ processResourceId, workspaceId, appId }) => ({
        scope: { workspaceId, appId },
        mapKey: getConversationMapKey({ workspaceId, appId }, processResourceId),
      })),
      // Ahead of the map-hit filter so that leaving a scope and returning to it reads as a change
      // and refetches, instead of collapsing into a single suppressed emission.
      distinctUntilChanged((previous, current) => previous.mapKey === current.mapKey),
      withLatestFrom(this.#store.select(ConversationSelectors.conversationProcessMap)),
      filter(([{ mapKey }, conversationProcessMap]) => !!conversationProcessMap[mapKey]),
      switchMap(([{ scope, mapKey }, conversationProcessMap]) => {
        const conversationId = conversationProcessMap[mapKey]
        return this.#conversationApiService.fetchConversation(conversationId).pipe(
          map((conversation) => {
            if (!conversation) {
              return ConversationActions.loadConversationFailure({
                error: 'Failed to load conversation',
              })
            }
            return isConversationInScope(conversation, scope)
              ? ConversationActions.loadConversationSuccess({ conversation })
              : ConversationErrorActions.conversationEvicted({ conversationId })
          }),
          catchError((error) => {
            console.error('[Conversation Error] Failed to load conversation:', error)
            return of(
              ConversationErrorActions.conversationLoadFailed({
                error,
                conversationId,
              })
            )
          })
        )
      })
    )
  )

  // TODO: Uncomment this once we implement a two step keep flow: keep ui and keep data model changes
  // generateUiStreamEnded = createEffect(() =>
  //   this.#actions$.pipe(
  //     ofType(AiConversationServiceActions.conversationMessageStreamEnded),
  //     withLatestFrom(
  //       this.#store.select(ConversationSelectors.activeThread),
  //       this.#store.select(selectDataModelEntitiesLeaves)
  //     ),
  //     map(([data, activeThread, dataModel]) => {
  //       const message = activeThread?.messages.find((message) => message.id === data.messageId)
  //       const agent = message?.executionPlan.actions[0]?.agent.agent

  //       return {
  //         message,
  //         agent,
  //         dataModel,
  //       }
  //     }),
  //     filter(({ agent }) => agent === AgentName.AiDesigner),
  //     map(({ message, dataModel }) => {
  //       const threadOutcome = JSON.parse(message.outcome.outcomes[0].textual.content)
  //       const dataModelChanges = DesignerService.extractDataModelChangesFromAiConfig(
  //         {
  //           interface: threadOutcome.interface,
  //           sectionsDetails: threadOutcome.sectionsDetails,
  //         },
  //         threadOutcome.mapped_fields?.map((field) => ({
  //           ...field,
  //           dataModelEntity: dataModel.find(({ name }) => name === field.model),
  //         }))
  //       )

  //       return ConversationActions.updateMessageContextData({
  //         messageId: message.id,
  //         contextData: {
  //           key: ContextDataKeys.UIAiDesignerSummaryChanges,
  //           itemType: ConversationThreadContextDataItemType.GenericString,
  //           valueGenericString: JSON.stringify(dataModelChanges),
  //           audit: null,
  //         },
  //       })
  //     })
  //   )
  // )

  keepOutcomeSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        AiAnalystActions.keepOutcomeSuccess,
        AiDesignerActions.keepOutcomeSuccess,
        AiDeveloperActions.keepOutcomeSuccess,
        AiArchitectActions.keepOutcomeSuccess,
        AiDesignerActions.keepDataModelDraftSuccess
      ),
      map(() => ConversationActions.keepOutcomeSuccess())
    )
  )

  discardOutcomeSuccess = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        AiAnalystActions.discardOutcomeSuccess,
        AiDesignerActions.discardOutcomeSuccess,
        AiDeveloperActions.discardOutcomeSuccess,
        AiArchitectActions.discardOutcomeSuccess
      ),
      map(() => ConversationActions.discardOutcomeSuccess())
    )
  )

  keepThread = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.keepOutcomeSuccess),
      switchMap(() => {
        const currentOutcome = this.#store.selectSignal(
          ConversationSelectors.currentOutcomeBeingKept
        )()
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()
        const agent = this.#store.selectSignal(ConversationSelectors.currentMessageAgent)()
        // Get the message with the agent action whose outcome is displayed
        const messageWithOutcome = this.#store.selectSignal(
          ConversationSelectors.latestAgentMessageWithAgentAction
        )()

        // Determine finalize and outcomeIds based on designer agent and outcome states
        let finalize = true
        let outcomeIds: string[] | undefined

        if (agent?.agent === AgentName.AiDesigner) {
          const isUiFinished = this.#store.selectSignal(ConversationSelectors.isUiOutcomeFinished)()
          const isDataModelFinished = this.#store.selectSignal(
            ConversationSelectors.isDataModelOutcomeFinished
          )()
          const uiOutcome = this.#store.selectSignal(ConversationSelectors.uiOutcome)()
          const dataModelOutcome = this.#store.selectSignal(
            ConversationSelectors.dataModelOutcome
          )()

          if (!isUiFinished && uiOutcome?.id) {
            // Collapse to a single step when there are no actual data-model
            // changes to keep next. A DM outcome can exist with empty
            // mapped_fields and still yield no changes (the buttons gate on the
            // same signal), so finalize on the UI keep and keep the empty DM
            // outcome alongside it to avoid leaving a dangling outcome.
            const hasDataModelChanges = !!this.#store.selectSignal(
              ConversationSelectors.aiGeneratedUiDataModelChanges
            )()
            finalize = !hasDataModelChanges
            outcomeIds =
              !hasDataModelChanges && dataModelOutcome?.id
                ? [uiOutcome.id, dataModelOutcome.id]
                : [uiOutcome.id]
          } else if (isUiFinished && !isDataModelFinished && dataModelOutcome?.id) {
            // Keep data model outcome, finalize (no more outcomes to keep)
            finalize = true
            outcomeIds = [dataModelOutcome.id]
          }
        }

        return this.#aiConversationService
          .addStoreMessage({
            prompt: `Keeping ${currentOutcome ?? 'outcome'}.`,
            type: MessageType.UserMsg,
          })
          .pipe(
            switchMap(() =>
              // Update the context of the message with the outcome before keeping the thread
              messageWithOutcome?.id
                ? this.#aiConversationContextBuilderService.buildIntentUnderstandingMessageContext()
                : of(null)
            ),
            switchMap((contextData) => {
              if (messageWithOutcome?.id && contextData) {
                const newItemsFromMessage = messageWithOutcome.contextData?.items ?? []
                const deduplicatedItems = _uniqBy(
                  'key',
                  [...newItemsFromMessage, ...contextData.items].reverse()
                ).reverse()

                const requestBody: PatchMessageContextRequest = {
                  context: { items: deduplicatedItems },
                }
                return this.#conversationApiService
                  .patchMessageContext(conversationId, threadId, messageWithOutcome.id, requestBody)
                  .pipe(
                    tap(() => {
                      // Update local store with the patched context
                      this.#store.dispatch(
                        ConversationActions.patchMessageContextItemsApiResponse({
                          threadId,
                          messageId: messageWithOutcome.id,
                          context: { items: deduplicatedItems },
                        })
                      )
                    }),
                    map(() => null)
                  )
              }
              return of(null)
            }),
            switchMap(() =>
              this.#conversationApiService.keepThread(conversationId, threadId, {
                messageId: messageWithOutcome?.id,
                outcomeIds,
                shouldFinalize: finalize,
              })
            ),
            map((response) =>
              ConversationActions.keepThreadApiResponse({
                conversationId,
                threadId,
                agent,
                pendingChanges: response?.pendingChanges ?? [],
                finalize,
              })
            ),
            catchError((error) =>
              of(
                ConversationErrorActions.keepOutcomeFailed({
                  error,
                  threadId,
                  conversationId,
                })
              )
            )
          )
      })
    )
  )

  discardOutcomeThread = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.discardOutcomeSuccess),
      switchMap(() => {
        const currentOutcome = this.#store.selectSignal(
          ConversationSelectors.currentOutcomeBeingKept
        )()
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()
        // Get the message with the agent action whose outcome would be discarded
        const messageWithOutcome = this.#store.selectSignal(
          ConversationSelectors.latestAgentMessageWithAgentAction
        )()

        return this.#aiConversationService
          .addStoreMessage({
            prompt: `Discarding ${currentOutcome ?? 'outcome'}.`,
            type: MessageType.UserMsg,
          })
          .pipe(
            switchMap(() =>
              this.#conversationApiService.discardThread(conversationId, threadId, {
                messageId: messageWithOutcome?.id,
                shouldFinalize: true,
              })
            ),
            map(() =>
              ConversationActions.discardThreadApiResponse({
                conversationId,
                threadId,
              })
            )
          )
      })
    )
  )

  discardThread = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationActions.discardThreadClick),
        switchMap((action) =>
          this.#aiConversationService.discardThread(action.addDiscardMessage, action.message)
        )
      ),
    { dispatch: false }
  )

  cancelIntentUnderstanding = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationActions.cancelIntentUnderstandingClick),
        withLatestFrom(
          this.#store.select(ConversationSelectors.pendingMessage),
          this.#store.select(ConversationSelectors.activeConversationExists)
        ),
        switchMap(([, pendingMessage, activeConversationExists]) => {
          this.#store.dispatch(
            activeConversationExists
              ? ConversationActions.defaultThreadMessageCancelled()
              : ConversationActions.conversationCreationCancelled()
          )
          if (!pendingMessage) {
            return EMPTY
          }
          return this.#aiConversationService
            .implementHintedAction({
              message: pendingMessage.message,
              context: { items: [] },
              target: { name: null, targetType: null },
              userPromptRequired: false,
              agent: {
                action: AgentAction.NoopStore,
                agent: AgentName.ConnectedGraph,
              },
            })
            .pipe(switchMap(() => this.#aiConversationService.discardThread()))
        })
      ),
    { dispatch: false }
  )

  discardThreadOnError = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiConversationServiceActions.conversationMessageStreamEnded),
        withLatestFrom(this.#store.select(ConversationSelectors.errorOutcomeDetails)),
        filter(([, errorDetails]) => errorDetails?.hasError ?? false),
        tap(([, errorDetails]) => {
          // Log the backend error message for debugging
          console.error(
            '[Conversation Error] Agent returned error outcome:',
            errorDetails.errorMessage
          )

          // Discard thread without adding a message (error already shown via virtual message)
          this.#aiConversationService.discardThread(false).subscribe()
        })
      ),
    { dispatch: false }
  )

  discardThreadOnStreamError = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(AiConversationServiceActions.conversationMessageStreamError),
        filter(() => this.#currentlyHandlingErrorForMessage === null),
        tap(({ messageId, threadId, conversationId }) => {
          console.error('[Conversation Error] Stream connection failed or was interrupted', {
            messageId,
            threadId,
            conversationId,
          })
          this.#currentlyHandlingErrorForMessage = messageId
        }),
        exhaustMap(() =>
          // First, add an error message to the thread
          this.#aiConversationService
            .storeErrorMessage(
              'Seems like something went wrong with your request. I am waiting on further instructions.'
            )
            .pipe(
              // After the error message is added, discard the thread
              switchMap(() =>
                this.#aiConversationService.discardThread(
                  false // Don't add another message since we already added the error message
                )
              ),
              tap(() => {
                this.#currentlyHandlingErrorForMessage = null
              }),
              catchError((error) => {
                console.error('[Conversation Error] Failed to handle stream error:', error)
                // Even if storeErrorMessage fails, still try to discard the thread
                return this.#aiConversationService.discardThread(false).pipe(
                  tap(() => {
                    this.#currentlyHandlingErrorForMessage = null
                  }),
                  catchError(() => {
                    console.error(
                      '[Conversation Error] Failed to discard thread after storeErrorMessage failure'
                    )
                    this.#currentlyHandlingErrorForMessage = null
                    return EMPTY
                  })
                )
              })
            )
        )
      ),
    { dispatch: false }
  )

  onKeepOutcomeClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.keepOutcomeClick),
      map(() => {
        const currentMessageAgent = this.#store.selectSignal(
          ConversationSelectors.currentMessageAgent
        )()

        switch (currentMessageAgent?.agent) {
          case AgentName.AiAnalyst:
            return AiAnalystActions.keepOutcome()
          case AgentName.AiDesigner:
            return AiDesignerActions.keepOutcome()
          case AgentName.AiDeveloper:
            return AiDeveloperActions.keepOutcome()
          case AgentName.AiArchitect:
            return AiArchitectActions.keepOutcome()
          default:
            throw new Error('No agent found to keep outcome')
        }
      })
    )
  )

  onDiscardOutcomeClick = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.discardOutcomeClick),
      map(() => this.#store.selectSignal(ConversationSelectors.currentMessageAgent)()),
      filter((agent) => !!agent),
      map((agent) => {
        switch (agent.agent) {
          case AgentName.AiAnalyst:
            return AiAnalystActions.discardOutcome()
          case AgentName.AiDesigner:
            return AiDesignerActions.discardOutcome()
          case AgentName.AiDeveloper:
            return AiDeveloperActions.discardOutcome()
          case AgentName.AiArchitect:
            return AiArchitectActions.discardOutcome()
          case AgentName.AiAssistant:
            return AiAssistantActions.discardOutcome()
          default:
            throw new Error('No agent found to discard outcome')
        }
      })
    )
  )

  onKeepExecutionPlanUpdates = createEffect(() =>
    this.#actions$.pipe(
      ofType(ExecutionPlanActions.keepExecutionPlanUpdates),
      switchMap((action) => {
        const currentExecutionPlan = this.#store.selectSignal(
          ConversationSelectors.currentExecutionPlan
        )()
        const newExecutionPlan = this.#store.selectSignal(ConversationSelectors.newExecutionPlan)()
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.defaultThreadId)()
        const messages = this.#store.selectSignal(
          ConversationSelectors.messagesWithValidPlansNotFinished
        )()

        if (
          isEmptyExecutionPlan(currentExecutionPlan) ||
          isEmptyExecutionPlan(newExecutionPlan) ||
          !conversationId ||
          !threadId ||
          !messages.length
        ) {
          console.error('Missing execution plans or thread info', {
            currentExecutionPlan,
            newExecutionPlan,
            conversationId,
            threadId,
            messages: messages.length,
          })
          return of(
            ExecutionPlanActions.replaceThreadMessageExecutionPlanSuccess({
              conversationId,
              threadId,
            })
          )
        }

        const resultingExecutionPlan = applyExecutionPlanChanges(
          currentExecutionPlan,
          newExecutionPlan,
          action.plan
        )

        // Get the second-to-last message (the one we want to update)
        const messageToUpdate = this.#store.selectSignal(
          ConversationSelectors.messageWithCurrentExecutionPlan
        )()

        const replaceInput: ReplaceMessageExecutionPlanRequest = {
          newMessage: 'The execution plan has been updated with the selected changes.',
          contextData: { items: [] },
          executionPlan: {
            actions: resultingExecutionPlan.actions.map((action) => ({
              ...action,
              target: action.target || { name: null, targetType: null },
              parallel: action.parallel ?? false,
            })),
          },
        }

        return this.#conversationApiService
          .replaceMessagePlan(conversationId, threadId, messageToUpdate.id, replaceInput)
          .pipe(
            map(() =>
              ExecutionPlanActions.replaceThreadMessageExecutionPlanSuccess({
                conversationId,
                threadId,
              })
            ),
            catchError((error) => {
              console.error('[Conversation Error] Failed to keep execution plan updates:', error)
              return of(
                ConversationErrorActions.executionPlanUpdateFailed({
                  error,
                  updateType: ExecutionPlanUpdateType.Keep,
                  conversationId,
                  threadId,
                })
              )
            })
          )
      })
    )
  )

  onDiscardExecutionPlanUpdates = createEffect(() =>
    this.#actions$.pipe(
      ofType(ExecutionPlanActions.discardExecutionPlanUpdate),
      switchMap(() => {
        const currentExecutionPlan = this.#store.selectSignal(
          ConversationSelectors.currentExecutionPlan
        )()
        const newExecutionPlan = this.#store.selectSignal(ConversationSelectors.newExecutionPlan)()
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const threadId = this.#store.selectSignal(ConversationSelectors.defaultThreadId)()
        const messages = this.#store.selectSignal(
          ConversationSelectors.messagesWithValidPlansNotFinished
        )()

        if (
          !currentExecutionPlan ||
          !newExecutionPlan ||
          !conversationId ||
          !threadId ||
          !messages.length
        ) {
          console.error('Missing execution plans or thread info', {
            currentExecutionPlan,
            newExecutionPlan,
            conversationId,
            threadId,
            messages: messages.length,
          })
          return of(
            ExecutionPlanActions.replaceThreadMessageExecutionPlanSuccess({
              conversationId,
              threadId,
            })
          )
        }

        // Only apply target updates from new execution plan to current execution plan
        const { executionPlan: resultingExecutionPlan } = assignNewTargetsToCurrentExecutionPlan(
          currentExecutionPlan,
          newExecutionPlan
        )

        // Get the second-to-last message (the one we want to update)
        const messageToUpdate = this.#store.selectSignal(
          ConversationSelectors.messageWithCurrentExecutionPlan
        )()

        const replaceInput: ReplaceMessageExecutionPlanRequest = {
          newMessage: 'The execution plan has been discarded but targets have been updated.',
          contextData: { items: [] },
          executionPlan: {
            actions: resultingExecutionPlan.actions.map((action) => ({
              ...action,
              target: action.target || { name: null, targetType: null },
              parallel: action.parallel ?? false,
            })),
          },
        }

        return this.#conversationApiService
          .replaceMessagePlan(conversationId, threadId, messageToUpdate.id, replaceInput)
          .pipe(
            map(() =>
              ExecutionPlanActions.replaceThreadMessageExecutionPlanSuccess({
                conversationId,
                threadId,
              })
            ),
            catchError((error) => {
              console.error('[Conversation Error] Failed to discard execution plan updates:', error)
              return of(
                ConversationErrorActions.executionPlanUpdateFailed({
                  error,
                  updateType: ExecutionPlanUpdateType.Delete,
                  conversationId,
                  threadId,
                })
              )
            })
          )
      })
    )
  )

  reloadDefaultThread = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        ExecutionPlanActions.replaceThreadMessageExecutionPlanSuccess,
        ConversationActions.keepThreadApiResponse,
        ConversationActions.discardThreadApiResponse
      ),
      filter((action) => {
        // For keepThreadApiResponse, only proceed if there are no pending changes to handle.
        // When finalize is false, pending changes are deferred until the final keep call,
        // so we should still reload the default thread.
        if (action.type === ConversationActions.keepThreadApiResponse.type) {
          return !action.finalize || !action.pendingChanges || action.pendingChanges.length === 0
        }
        // For other actions, always proceed
        return true
      }),
      switchMap(({ conversationId }) => {
        const defaultThreadId = this.#store.selectSignal(ConversationSelectors.defaultThreadId)()

        return this.#aiConversationService
          .fetchConversationThreadById({ conversationId, threadId: defaultThreadId })
          .pipe(
            map((thread) => {
              if (thread) {
                return ConversationActions.threadMessageFetchSuccess({ thread })
              } else {
                console.error('Failed to fetch default thread with id:', defaultThreadId)
                return ConversationActions.threadMessageFetchSuccess({ thread: null })
              }
            }),
            catchError((error) => {
              console.error('[Conversation Error] Failed to reload default thread:', error)
              return of(
                ConversationErrorActions.conversationLoadFailed({
                  error,
                  conversationId,
                })
              )
            })
          )
      })
    )
  )

  reloadAgentThread = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        AiConversationServiceActions.conversationMessageStreamEnded,
        ConversationActions.keepThreadApiResponse
      ),
      switchMap(({ conversationId, threadId }) =>
        this.#aiConversationService.fetchConversationThreadById({ conversationId, threadId }).pipe(
          map((thread) => {
            if (thread) {
              return ConversationActions.threadMessageFetchSuccess({ thread })
            } else {
              console.error(`Thread ${threadId} not found in conversation ${conversationId}`)
              return { type: 'NO_OP' }
            }
          }),
          catchError((error) => {
            console.error('[Conversation Error] Failed to reload agent thread:', error)
            return EMPTY
          })
        )
      )
    )
  )

  handlePendingExecutionPlanChanges = createEffect(() =>
    this.#actions$.pipe(
      ofType(ConversationActions.keepThreadApiResponse),
      filter(
        ({ pendingChanges, finalize }) => finalize && pendingChanges && pendingChanges.length > 0
      ),
      switchMap((action) => {
        const defaultThreadId = this.#store.selectSignal(ConversationSelectors.defaultThreadId)()
        const conversationId = this.#store.selectSignal(
          ConversationSelectors.activeConversationId
        )()
        const currentExecutionPlan = this.#store.selectSignal(
          ConversationSelectors.currentExecutionPlan
        )()
        const messageToUpdate = this.#store.selectSignal(
          ConversationSelectors.messageWithCurrentExecutionPlan
        )()

        if (!defaultThreadId || !conversationId) {
          console.error('Missing defaultThreadId or conversationId for pending changes')
          return of(null)
        }

        // Extract all actions from all pending changes
        const allActions = action.pendingChanges.flatMap(
          (change) => change.executionPlan?.actions || []
        )

        if (allActions.length === 0) {
          return of(null)
        }

        // Calculate the diff between current and new execution plan
        const diff = getExecutionPlanEnhanceDiff(currentExecutionPlan?.actions || [], allActions)

        // Check if the diff is empty (no added, updated, or deleted actions)
        const isDiffEmpty =
          diff.added.length === 0 && diff.updated.length === 0 && diff.deleted.length === 0

        if (isDiffEmpty && messageToUpdate) {
          // Skip addThreadMessage and go directly to replaceThreadMessageExecutionPlan
          const replaceInput: ReplaceMessageExecutionPlanRequest = {
            newMessage: 'No updates necessary for the execution plan.',
            contextData: { items: [] },
            executionPlan: {
              actions: allActions.map((action) => ({
                ...action,
                target: action.target || { name: null, targetType: null },
                parallel: action.parallel ?? false,
              })),
            },
          }

          return this.#conversationApiService
            .replaceMessagePlan(conversationId, defaultThreadId, messageToUpdate.id, replaceInput)
            .pipe(
              map(() =>
                ExecutionPlanActions.replaceThreadMessageExecutionPlanSuccess({
                  conversationId,
                  threadId: defaultThreadId,
                })
              ),
              catchError((error) => {
                console.error(
                  '[Conversation Error] Failed to handle pending execution plan changes (replace):',
                  error
                )
                return of(
                  ConversationErrorActions.executionPlanUpdateFailed({
                    error,
                    updateType: ExecutionPlanUpdateType.ApplySuggestions,
                    conversationId,
                    threadId: defaultThreadId,
                  })
                )
              })
            )
        }

        // Create a message on the default thread with all pending actions in the hint
        const addMessageRequest: AddThreadMessageRequest = {
          message: 'There are some pending execution plan changes.',
          documents: [],
          contextData: { items: [] },
          hint: {
            autoStart: false,
            externallyComputedPlan: true,
            actions: allActions.map((action) => ({
              ...action,
              target: action.target ?? { name: null, targetType: null },
            })),
          },
        }
        return this.#conversationApiService
          .addThreadMessage(conversationId, defaultThreadId, addMessageRequest)
          .pipe(
            map((response) => {
              if (response) {
                return ExecutionPlanActions.pendingExecutionPlanChangesAfterKeepSavedToNewMessage({
                  threadMessage: response,
                  threadId: defaultThreadId,
                })
              }
              return null
            }),
            filter((action) => action !== null),
            catchError((error) => {
              console.error(
                '[Conversation Error] Failed to handle pending execution plan changes (addThreadMessage):',
                error
              )
              return of(
                ConversationErrorActions.executionPlanUpdateFailed({
                  error,
                  updateType: ExecutionPlanUpdateType.ApplySuggestions,
                  conversationId,
                  threadId: defaultThreadId,
                })
              )
            })
          )
      })
    )
  )

  onFileUploaded = createEffect(() =>
    this.#actions$.pipe(
      ofType(TypeAreaActions.fileSelected, ConversationActions.fileSelectedFromMessage),
      filter(() => this.#store.selectSignal(ConversationSelectors.attachmentsAllowed)()),
      map(({ files }) => this.#mapFilesToUploadContextFile(files)),
      filter((files) => {
        const hasFiles = !!files?.length
        return hasFiles
      }),
      withLatestFrom(this.#store.select(ConversationSelectors.allowedFileTypes)),
      filter(([files, allowedFileTypes]) => {
        const allowed = this.#areFilesAllowed(files, allowedFileTypes)
        return allowed
      }),
      map(([files]) => ConversationActions.uploadedFilesUpdated({ files }))
    )
  )

  syncConversationProcessMapToLocalStorage = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(
          ConversationActions.createConversationApiResponse,
          ConversationActions.clearActiveConversation,
          ExecutionPlanActions.deleteExecutionPlan,
          ConversationActions.conversationCreationCancelled,
          ConversationActions.conversationClosedSuccess,
          ConversationActions.conversationClosedFailure,
          ConversationErrorActions.conversationEvicted
        ),
        withLatestFrom(this.#store.select(ConversationSelectors.conversationProcessMap)),
        tap(([, conversationProcessMap]) => {
          localStorage.setItem(conversationProcessMapKey, JSON.stringify(conversationProcessMap))
        })
      ),
    { dispatch: false }
  )

  newSession = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationActions.newSessionRequested),
        switchMap(() =>
          this.#flxModalService
            .show(
              ConfirmationModalComponent,
              {
                closeOnBackdropClick: false,
                backdropClass: 'overlay-backdrop',
                appendTo: 'body',
              },
              {
                title: 'Start new session',
                message: 'This clears the current conversation and can’t be undone.',
                btnOkText: 'Start new',
                btnCancelText: 'Cancel',
                btnOkType: 'danger',
              }
            )
            .data$.pipe(
              filter((data) => data.confirm),
              switchMap(() =>
                this.#aiExecutionPlanService.deleteExecutionPlan().pipe(
                  tap(() => {
                    this.#store.dispatch(ConversationActions.documentSelected({ document: null }))
                  })
                )
              )
            )
        )
      ),
    { dispatch: false }
  )

  processDeleted = createEffect(() =>
    this.#actions$.pipe(
      ofType(ProcessActions.processDeleted),
      withLatestFrom(
        this.#store.select(ConversationSelectors.conversationProcessMap),
        this.#store.select(ConversationSelectors.conversationRouteScope)
      ),
      switchMap(([{ processResourceId }, conversationProcessMap, scope]) => {
        const conversationId =
          conversationProcessMap[getConversationMapKey(scope, processResourceId)]
        if (!conversationId) {
          return EMPTY
        }
        return this.#aiConversationService.closeConversation(conversationId).pipe(
          map(() => ConversationActions.conversationClosedSuccess({ conversationId })),
          catchError(() => of(ConversationActions.conversationClosedFailure({ conversationId })))
        )
      })
    )
  )

  autoStartAssistantAction = createEffect(
    () =>
      this.#actions$.pipe(
        ofType(ConversationActions.autoStartAssistantAction),
        switchMap(({ action, skipUserConfirmation }) =>
          this.#aiExecutionPlanService.runExecutionPlanAction(action, skipUserConfirmation, null)
        )
      ),
    { dispatch: false }
  )

  #mapFilesToUploadContextFile(files: File[]): UploadContextFile[] {
    return files.map((file) => ({
      file,
      name: file.name,
      type: file.type,
      id: uuid(),
    }))
  }

  #areFilesAllowed(files: UploadContextFile[], allowedFileTypes: string[] = []): boolean {
    return _every(
      ({ name, type }) =>
        allowedFileTypes.includes(type) ||
        allowedFileTypes.some((allowedFileType) => name.endsWith(allowedFileType))
    )(files)
  }
}
